import React, { useState, useEffect, memo } from "react";
import PT from "prop-types";
import Modal, { Content, Header } from "_c/Modals";
import { useTranslation } from "react-i18next";
import { Spin } from "_c/preloader";
import { ONBOARDING_NS as ns } from "@/i18n/ns";
import Step from "./Step";
import * as S from "./styled";
import { AnimatedList } from "_c/Animated";
import Hammer from "react-hammerjs";
import {
  ONBOARDING_SHOW_DELAY as visibilityDelay,
  ONBOARDING_CONFIG as baseConfig,
} from "@/config";
import { analyticsAction } from "@/utils/analytics";

const Onboarding = ({ domain, onClose, config: { steps } }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { t, ready } = useTranslation(["common", ns], {
    useSuspense: false,
  });
  useEffect(() => {
    // preload images except the first one
    for (const step of steps.slice(1)) {
      const img = new Image();
      img.src = `/onboarding/${domain}/${step}.gif`;
    }
  }, []);
  const goNext = () =>
    currentStep >= steps.length - 1
      ? onClose()
      : setCurrentStep((step) => step + 1);
  const goPrev = () =>
    currentStep <= 0
      ? setCurrentStep(steps.length - 1)
      : setCurrentStep((step) => step - 1);
  useEffect(() => {
    const listener = ({ key }) => {
      if (key === "ArrowLeft") {
        goPrev();
      } else if (key === "ArrowRight") {
        goNext();
      }
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [currentStep]);
  const handleSwipe = ({ deltaX }) => {
    if (deltaX > 0) {
      goPrev();
    } else {
      goNext();
    }
  };
  if (!ready) return <Spin />;
  return (
    <>
      <Header onClick={onClose}>{t("common:close")}</Header>
      <Hammer direction="DIRECTION_HORIZONTAL" onSwipe={handleSwipe}>
        <Content
          css={`
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            color: var(--font-color-main);
          `}
        >
          <AnimatedList.Container>
            <AnimatedList.Item itemKey={currentStep}>
              <Step
                goNext={goNext}
                step={steps[currentStep]}
                domain={domain}
                t={t}
              />
            </AnimatedList.Item>
          </AnimatedList.Container>
          <div
            css={`
              display: flex;
              justify-content: center;
              padding-top: 25px;
              padding-bottom: 5px;
              align-items: center;
              margin-top: auto;
            `}
          >
            {steps.map((step, idx) => (
              <S.Dot
                isActive={idx === currentStep}
                role="button"
                onClick={() => setCurrentStep(idx)}
                key={step}
              />
            ))}
          </div>
        </Content>
      </Hammer>
    </>
  );
};

const OnboardingModal = (props) => {
  const { config } = props;
  const persistKey = `onbrd_${props.domain}:${config.v}`;
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    const shouldShow = !localStorage.getItem(persistKey);
    const timeout = setTimeout(() => {
      if (shouldShow) {
        setIsOpen(true);
      }
    }, visibilityDelay);
    return () => clearTimeout(timeout);
  }, [persistKey]);
  const onClose = () => {
    localStorage.setItem(persistKey, "1");
    setIsOpen(false);
    analyticsAction({
      category: "onboarding_inner",
      action: "onbclose",
    });
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Onboarding onClose={onClose} config={config} {...props} />
    </Modal>
  );
};

const OnboardingModalConfigProxy = (props) => {
  const config = baseConfig[props.domain];
  if (!config?.steps?.length) return null;
  return <OnboardingModal {...props} config={config} />;
};
OnboardingModalConfigProxy.propTypes = {
  domain: PT.string.isRequired,
};

export default memo(OnboardingModalConfigProxy);
